import React from 'react';
import { useSelector } from 'react-redux';

import { selectUser } from 'entities/user/selectors';
import User from 'entities/user/model';
import { RenderMachine } from 'components/Flow/RenderMachine';
import { Privileges } from 'utils/privileges';
import { useNonprofitInfoQuery } from 'generated/graphql';
import { usePayPalVerificationCode } from 'PayPalVerificationCodeProvider';

import { getAllowedSteps } from '../getAllowedSteps';

import { nonprofitOnboardingMachine } from './machine';
import { NonprofitOnboardingFlowContext } from './context';

const NonprofitOnboardingFlowInner = ({ user }: { user: User }) => {
  const payPalVerificationCode = usePayPalVerificationCode();
  const { data: nonprofitInfo, loading: nonprofitInfoLoading } = useNonprofitInfoQuery({
    skip: !user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS),
  });
  if (nonprofitInfoLoading) {
    return null;
  }

  const verifiedUsers = nonprofitInfo?.nonprofitInfo?.verifiedUsers || [];

  const hasVerifiedUsers = nonprofitInfo?.nonprofitInfo?.hasVerifiedUsers !== false;
  const isBlocked = !(user?.privileges?.length > 1);
  // @NOTE-DP: if the nonprofit already has verified users AND he's not blocked, then we don't need to prompt for verification
  const verificationNotNeeded =
    hasVerifiedUsers &&
    !isBlocked &&
    // @NOTE-DP: if verificaiton code is there, we really mean to verify
    !payPalVerificationCode;

  const originalAllowedSteps = getAllowedSteps(user);
  const allowedSteps = verificationNotNeeded
    ? originalAllowedSteps.filter((step) => step !== 'verification')
    : originalAllowedSteps;

  return (
    <RenderMachine
      machine={nonprofitOnboardingMachine}
      options={{
        context: {
          userType: user?.type,
          dismissedSteps: user?.get('onboarding')?.toJS(),
          payPalVerificationCode,
          allowedSteps,
          nonprofit: {
            payPalModel: user.organization?.payPalModel,
            paypalCharityType: user.organization?.paypalCharityType,
            nonprofitEntityType: user.organization?.nonprofitEntityType,
            nonprofitDataValidatedAt: user.organization?.nonprofitDataValidatedAt,
            verifiedUsers,
          },
        },
      }}
      context={NonprofitOnboardingFlowContext}
    />
  );
};

export const NonprofitOnboardingFlow = (): JSX.Element | null => {
  const user = useSelector(selectUser) as User;

  if (!user) {
    return null;
  }

  if (
    user.isAdmin ||
    user.organization?.isCompany() ||
    user.hasPrivilege(Privileges.ORGANIZATION_SKIP_ONBOARDING)
  ) {
    return null;
  }

  return <NonprofitOnboardingFlowInner user={user} />;
};
